import React, { useCallback, useImperativeHandle, useLayoutEffect, useMemo, useState } from 'react';

import { Box, Collapse, IconButton, SvgIcon, Typography } from '@mui/material';

import IconCopy from 'app/ui-kit/icons/Copy.svg';
import isEmpty from 'utils/is-empty';

import useStyles from './styles';

import { COLORS, NAMES_MAP } from '../../constants';

interface Props {
    score: string | number;
    severity: string;
    name: string;
    vector?: string;
    vectorItems?: Record<string, React.ReactNode>;
}

export interface ScorePaneRef {
    setIsOpen: (isOpen: boolean) => void;
}

const ScorePane: React.ForwardRefRenderFunction<ScorePaneRef, Props> = (
    { score, name, vector, vectorItems, severity },
    ref
) => {
    const { classes } = useStyles();
    const [isOpen, setIsOpen] = useState(false);
    const toggleOpen = useCallback(() => setIsOpen((state) => !state), []);

    useImperativeHandle(ref, () => ({ setIsOpen }), []);

    useLayoutEffect(() => {
        // Window is not available during SSR
        setIsOpen(name === 'EPSS' && window.innerWidth >= 960);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const hasVectorItems = useMemo(() => !!vectorItems && isEmpty(vectorItems) === false, [vectorItems]);

    return (
        <div key={name} className={classes.container}>
            <Box
                className={classes.pane}
                onClick={toggleOpen}
                style={{
                    background: COLORS.BACKGROUND[severity],
                    cursor: hasVectorItems ? 'pointer' : 'initial',
                    borderBottomLeftRadius: hasVectorItems && isOpen ? 0 : 18,
                    borderBottomRightRadius: hasVectorItems && isOpen ? 0 : 18
                }}
            >
                <Box display="flex" flexDirection="row">
                    <div className={classes.accent} style={{ background: COLORS.ACCENT[severity] }} />
                    <Typography variant="body1">{`${score} ${severity}`}</Typography>
                </Box>
                <Typography variant="body2">{name}</Typography>
            </Box>
            {hasVectorItems ? (
                <Collapse in={isOpen}>
                    <div>
                        {Object.entries(vectorItems as object).map(([key, value]) => (
                            <Box key={`${name}-${key}`} className={classes.vectorItem}>
                                <Typography variant="body2">{NAMES_MAP[key]}</Typography>
                                <Typography variant="body2">{value}</Typography>
                            </Box>
                        ))}
                    </div>

                    {vector ? (
                        <Box className={classes.vector}>
                            <Typography variant="body2">{vector}</Typography>
                            <IconButton size="small">
                                <SvgIcon component={IconCopy} />
                            </IconButton>
                        </Box>
                    ) : null}
                </Collapse>
            ) : null}
        </div>
    );
};

export default React.forwardRef(ScorePane);
